import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { getServerUrl } from './environment';
import { assignAppPath, getCurrentAppPath } from './route-mode';
import { clearAccessToken, getAccessToken, setAccessToken } from './session';
import ReviewerTokenService from '@/services/reviewerToken.service';
import TaskInviteTokenService from '@/services/taskInviteToken.service';

type RequestContext = {
  organizationId: string | null;
  workspaceId: string | null;
};

let requestContextGetter: () => RequestContext = () => ({
  organizationId: null,
  workspaceId: null,
});

export function setRequestContextGetter(getter: () => RequestContext) {
  requestContextGetter = getter;
}

/**
 * Read the current request context (org / workspace). For non-axios callers —
 * e.g. SSE `fetch` streams — that must set the `x-v2-org-id` / `x-v2-ws-id`
 * headers themselves, since a raw fetch bypasses the axios request interceptor
 * that normally injects them.
 */
export function getRequestContext(): RequestContext {
  return requestContextGetter();
}

const sessionScopeReasonMap: Record<string, string> = {
  AUTH_METHOD_MISMATCH: 'session_auth_mismatch',
  SSO_ORG_MISMATCH: 'session_org_mismatch',
  REQUIRES_SSO: 'requires_sso',
  SESSION_SCOPE_VIOLATION: 'session_scope_violation',
};

function redirectToLogin(reason = 'session_invalid') {
  clearAccessToken();

  const currentPath = getCurrentAppPath();
  if (currentPath.startsWith('/login')) return;

  const next = `/login?reason=${encodeURIComponent(reason)}&redirect=${encodeURIComponent(currentPath)}`;
  assignAppPath(next);
}

export const api = axios.create({
  withCredentials: true,
});

function requestPath(url = '') {
  try {
    return new URL(url, 'http://local').pathname;
  } catch {
    return url.split(/[?#]/, 1)[0];
  }
}

export function requiresSessionAuthentication(url = '') {
  const path = requestPath(url);
  return !(
    path === '/auth/login' ||
    path === '/auth/register' ||
    path === '/auth/refresh' ||
    path.startsWith('/review/auth/') ||
    path.startsWith('/v2/tasks/auth/')
  );
}

export function canRecoverSessionFromUnauthorized(url = '') {
  const path = requestPath(url);
  return !(
    path === '/auth/refresh' ||
    path === '/review/auth/refresh-token' ||
    path === '/v2/tasks/auth/refresh' ||
    path === '/auth/login' ||
    path === '/auth/register'
  );
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  config.baseURL = getServerUrl();

  const token = getAccessToken();
  const requiresAuth = requiresSessionAuthentication(config.url);
  const hasHmKey = Boolean(
    config.headers?.['x-hm-magic-key'] ?? (config.headers as unknown as Headers)?.get?.('x-hm-magic-key'),
  );

  if (requiresAuth && !hasHmKey) {
    const reviewerAuthHeader = ReviewerTokenService.getAuthHeader();
    const isReviewerResource = config.url?.includes('/candidate/') || config.url?.includes('/comments');
    const taskInviteHeader = TaskInviteTokenService.getAuthHeader();
    // The task-invite token covers /v2/tasks/* and /v2/reports/* (the report
    // overlay uses these). For other endpoints fall through to the org user
    // token if present.
    const isTaskInviteResource =
        TaskInviteTokenService.isActive() &&
        (config.url?.includes('/v2/tasks') ||
            config.url?.includes('/v2/reports/') ||
            config.url?.includes('/v2/application/'));

    if (reviewerAuthHeader && isReviewerResource) {
      config.headers.Authorization = reviewerAuthHeader;
    } else if (taskInviteHeader && isTaskInviteResource && !token) {
      // Only use the invite token when there's no normal org token —
      // org users with a valid session always get their full RBAC.
      config.headers.Authorization = taskInviteHeader;
    } else if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (taskInviteHeader && isTaskInviteResource) {
      // No org token at all → fall back to invite for these resources.
      config.headers.Authorization = taskInviteHeader;
    }
  }

  if (config.url?.includes('/v2/')) {
    const context = requestContextGetter();
    if (context.organizationId) config.headers['x-v2-org-id'] = context.organizationId;
    if (context.workspaceId) config.headers['x-v2-ws-id'] = context.workspaceId;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ code?: string; message?: string; accessToken?: string }>) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const errorCode = error.response?.data?.code;
    const originalHadHmKey = Boolean(
      originalRequest?.headers?.['x-hm-magic-key'] ??
        (originalRequest?.headers as unknown as Headers)?.get?.('x-hm-magic-key'),
    );

    if (error.response?.status === 403 && errorCode && sessionScopeReasonMap[errorCode]) {
      redirectToLogin(sessionScopeReasonMap[errorCode]);
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalHadHmKey &&
      canRecoverSessionFromUnauthorized(originalRequest.url)
    ) {
      originalRequest._retry = true;

      try {
        const reviewerAuthHeader = ReviewerTokenService.getAuthHeader();
        const isReviewerRequest = reviewerAuthHeader && originalRequest.headers.Authorization === reviewerAuthHeader;

        if (isReviewerRequest) {
          const refreshSuccess = await ReviewerTokenService.refreshAccessToken();
          const nextReviewerAuthHeader = ReviewerTokenService.getAuthHeader();

          if (refreshSuccess && nextReviewerAuthHeader) {
            originalRequest.headers.Authorization = nextReviewerAuthHeader;
            return api(originalRequest);
          }

          ReviewerTokenService.forceLogout();
          return Promise.reject(error);
        }

        const response = await api.post<{ accessToken: string }>('/auth/refresh');
        setAccessToken(response.data.accessToken);
        originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        redirectToLogin('session_expired');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export function getApiErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError<{ error?: string; message?: string; detail?: string }>(error)) {
    const data = error.response?.data;
    return data?.error || data?.message || data?.detail || error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}
